import { useEffect, useState } from 'react';
import { useGetAllConfigurationSettingsQuery } from 'src/services';
import { mergeConfigurationSettings } from 'src/pages/ConfigurationPage/utils/mergeConfigurationSettings';
import type { ConfigurationSettings } from 'src/services/configurationService';

/**
 * Loads all of a project's configuration settings versions, requesting each next page as the
 * previous one arrives and merging them newest first. `settings` starts as `initialSettings`
 * (null or an empty list, as the caller's effects expect).
 */
export const usePagedConfigurationSettings = (
  id: number | string,
  queryOptions: { skip: boolean; refetchOnMountOrArgChange?: boolean },
  initialSettings: ConfigurationSettings[] | null,
) => {
  const [settingsPage, setSettingsPage] = useState(1);
  const [settings, setSettings] = useState<ConfigurationSettings[] | null>(initialSettings);

  const { data: allConfigurationSettings, isLoading: isLoadingAllConfigurationSettings } =
    useGetAllConfigurationSettingsQuery({ id, page: settingsPage }, queryOptions);

  useEffect(() => {
    if (!allConfigurationSettings) {
      return;
    }

    setSettings((prevState) =>
      mergeConfigurationSettings(prevState, allConfigurationSettings.items),
    );

    if (allConfigurationSettings.page_info.has_next_page) {
      setSettingsPage(allConfigurationSettings.page_info.page + 1);
    }
  }, [allConfigurationSettings]);

  // Starts loading again from the first page, merging into the versions already loaded. A cached
  // first page is used as it is, so callers update it (createConfigurationSettings patches it) or
  // invalidate it (deployConfigurationSettings) before resetting.
  const resetToFirstPage = () => setSettingsPage(1);

  return { settings, isLoadingAllConfigurationSettings, resetToFirstPage };
};
